import { ReactNode, useContext } from 'react';

import AddElementsModal from '@atom/components/common/addElementsModal/AddElementsModal';
import RenameModal from '@atom/components/common/RenameModal';
import { List, Modal } from '@atom/mui';
import colors from '@atom/styles/colors';

import SubItemBranch from './SubItemBranch';
import SubItemRow from './SubItemRow';
import SubItemsContext, {
  defaultRowPolicy,
  SubItemModalType,
} from './SubItemsContext';
import { hasDownstreamChanges } from './subItemTreeReducer';

type SubItemTreeProps = {
  header?: ReactNode;
  extraModals?: ReactNode;
};

const SubItemTree = ({ header, extraModals }: SubItemTreeProps) => {
  const {
    rootAsset,
    rootNode,
    tree,
    permissions,
    rowPolicy = defaultRowPolicy,
    selectedId,
    scrollToId,
    onScrolled,
    onRowClick,
    approveChanges,
    openModal,
    activeModal,
    closeModal,
    approvalErrorOpen,
    closeApprovalError,
    renameElement,
    deleteElement,
    rejectChanges,
    batchCreateElements,
  } = useContext(SubItemsContext);

  const modalNode = activeModal?.node;
  const isOpen = (type: SubItemModalType): boolean =>
    activeModal?.type === type;

  return (
    <>
      {header}
      <List disablePadding>
        {rootNode ? (
          <>
            <SubItemRow
              node={rootNode.node}
              depth={0}
              isSelected={selectedId === rootAsset.id}
              isExpanded
              isLoading={tree.status[rootAsset.id] === 'loading'}
              hasDownstreamChanges={hasDownstreamChanges(tree, rootAsset.id)}
              shouldScroll={scrollToId === rootAsset.id}
              permissions={permissions}
              rowPolicy={rowPolicy}
              customIcon={rootNode.icon}
              expandable={false}
              onClick={onRowClick}
              onApprove={approveChanges}
              onOpenModal={openModal}
              onScrolled={onScrolled}
            />
            <SubItemBranch parentId={rootAsset.id} depth={1} />
          </>
        ) : (
          <SubItemBranch parentId={rootAsset.id} depth={0} />
        )}
      </List>
      {modalNode && (
        <>
          <AddElementsModal
            open={isOpen(SubItemModalType.ADD_ELEMENT)}
            onClose={closeModal}
            title="Add elements to asset"
            assetId={modalNode.id}
            assetName={modalNode.name}
            schemaId={modalNode.schemaId}
            onBatchCreate={batchCreateElements}
            confirmDataCy="elementAddAssetElementConfirm"
            cancelDataCy="elementAddAssetElementCancel"
          />
          <RenameModal
            open={isOpen(SubItemModalType.RENAME)}
            closeModal={closeModal}
            id={modalNode.id}
            name={modalNode.name}
            type="element"
            renameAction={renameElement}
          />
          <Modal
            open={isOpen(SubItemModalType.DELETE)}
            title="Delete Element"
            onCancel={closeModal}
            confirmButtonText="Delete"
            ConfirmButtonProps={{
              sx: { background: colors.brand.red },
              'data-cy': 'deleteElementModalConfirm',
            }}
            CancelButtonProps={{ 'data-cy': 'deleteElementModalCancel' }}
            onConfirm={() => {
              deleteElement(modalNode);
              closeModal();
            }}
          >
            Are you sure you want to delete this element?
          </Modal>
          <Modal
            open={isOpen(SubItemModalType.REJECT_CHANGES)}
            title="Reject Changes"
            onCancel={closeModal}
            confirmButtonText="Reject"
            ConfirmButtonProps={{ 'data-cy': 'rejectChangesModalConfirm' }}
            CancelButtonProps={{ 'data-cy': 'rejectChangesModalCancel' }}
            onConfirm={() => {
              rejectChanges(modalNode);
              closeModal();
            }}
          >
            Are you sure you want to reject the changes on this item?
          </Modal>
        </>
      )}
      {extraModals}
      <Modal
        open={approvalErrorOpen}
        title="Cannot Approve All Changes"
        confirmButtonText="OK"
        ConfirmButtonProps={{ 'data-cy': 'approvalErrorModalConfirm' }}
        onConfirm={closeApprovalError}
      >
        Some pending changes cannot be approved because the attribute is not
        editable. Please set the attribute to editable in the asset schema to
        approve or reject changes.
      </Modal>
    </>
  );
};

export default SubItemTree;
